import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadStorefronts } from "@/components/StorefrontEditor";

/**
 * Generate a comprehensive Manufacturer Quote Package PDF.
 * Multi-page: Cover, Container BOM, Unit Catalog, Building Spec, Unit Schedule, Site Summary.
 */
export async function exportBuildingPdf(building, summary, buildingId, snapshots = [], siteSummary = null) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // ------- Project constants (site-level) — sourced from siteSummary if provided -------
  const SITE = {
    buildings:        siteSummary?.total_buildings ?? 6,
    totalUnits:       siteSummary?.total_residential ?? 192,
    commercial:       siteSummary?.total_commercial ?? 8,
    studios:          siteSummary?.total_studios ?? 66,
    ones:             siteSummary?.total_1br ?? 66,
    twos:             siteSummary?.total_2br ?? 60,
    parkingTotal:     siteSummary?.parking_required ?? 93,
    parkingBasis:     siteSummary?.parking_basis ?? "Belize SI 157/2022 Reg 6(b): 1 stall / 500 SF habitable",
    livingSF:         siteSummary?.total_living_sf ?? 46080,
    avgUnitSize:      siteSummary?.avg_unit_size_sf ?? 208,
    lotAcres:         siteSummary?.lot_acres ?? 2.13,
    lotSF:            siteSummary?.lot_sf ?? 95832,
    location:         "San Pedro, Belize",
    totalContainers:  siteSummary?.total_containers ?? 152,
  };

  // Container BOM per building — read from current building's wings
  const per_building_by_type = summary.by_type || {};
  // Containers (all 40' in the new spec)
  const split_containers = Math.max(SITE.studios, SITE.ones);
  const full_2br = SITE.twos;
  const commercial_containers = SITE.commercial;
  const cont40_site = split_containers + full_2br + commercial_containers;
  const total_containers = cont40_site;

  const ut_map = Object.fromEntries(building.unit_types.map(u => [u.id, u]));

  // ===== shared draw helpers =====
  const bgDark = [11, 15, 20];
  const card   = [15, 23, 42];
  const accent = [252, 196, 25];
  const text   = [230, 237, 243];
  const muted  = [148, 163, 184];
  const good   = [34, 197, 94];

  const fillBg = () => { doc.setFillColor(...bgDark); doc.rect(0, 0, W, H, "F"); };

  const drawHeader = (title, subtitle) => {
    doc.setFillColor(...bgDark); doc.rect(0, 0, W, 60, "F");
    doc.setFillColor(...accent); doc.rect(0, 56, W, 2, "F");
    doc.setFont("courier", "bold"); doc.setFontSize(9);
    doc.setTextColor(...accent);
    doc.text("SUNRISE COMMUNITY · BELIZE · CONTAINER HOUSING", 36, 24);
    doc.setFont("helvetica", "bold"); doc.setFontSize(18);
    doc.setTextColor(...text);
    doc.text(title, 36, 46);
    if (subtitle) {
      doc.setFont("courier", "normal"); doc.setFontSize(9);
      doc.setTextColor(...muted);
      doc.text(subtitle, W - 36, 46, { align: "right" });
    }
  };

  const drawFooter = (pageNum) => {
    doc.setFont("courier", "normal"); doc.setFontSize(7);
    doc.setTextColor(...muted);
    doc.text(`MANUFACTURER QUOTE PACKAGE · ${new Date().toISOString().slice(0,10)}`, 36, H - 18);
    doc.text(`PAGE ${pageNum}`, W - 36, H - 18, { align: "right" });
  };

  // ============================================================
  // PAGE 1 — COVER
  // ============================================================
  fillBg();
  doc.setFont("courier", "bold"); doc.setFontSize(10);
  doc.setTextColor(...accent);
  doc.text("SHEET Q1.0  ·  MANUFACTURER QUOTE PACKAGE", 36, 50);

  doc.setFont("helvetica", "bold"); doc.setFontSize(40);
  doc.setTextColor(...text);
  doc.text("Sunrise Community", 36, 130);
  doc.setFont("helvetica", "normal"); doc.setFontSize(20);
  doc.setTextColor(...muted);
  doc.text("Container Housing · Belize", 36, 158);

  doc.setFont("courier", "normal"); doc.setFontSize(10);
  doc.setTextColor(...accent);
  doc.text("FOR PRICING & QUOTATION ONLY", 36, 184);

  // Big stats grid
  const grid = [
    { label: "BUILDINGS",         val: SITE.buildings },
    { label: "RESIDENTIAL UNITS", val: SITE.totalUnits },
    { label: "COMMERCIAL UNITS",  val: SITE.commercial },
    { label: "TOTAL CONTAINERS",  val: SITE.totalContainers },
    { label: "STUDIOS · 1BR · 2BR", val: `${SITE.studios}/${SITE.ones}/${SITE.twos}` },
    { label: "TOTAL LIVING SF",   val: SITE.livingSF.toLocaleString() },
    { label: "STORIES / BLDG",    val: 3 },
    { label: "LOT SIZE",          val: `${SITE.lotAcres} AC` },
  ];
  const col = 4, gap = 18;
  const cardW = (W - 72 - gap * (col - 1)) / col;
  grid.forEach((g, i) => {
    const r = Math.floor(i / col);
    const c = i % col;
    const x = 36 + c * (cardW + gap);
    const y = 220 + r * 110;
    doc.setFillColor(...card); doc.rect(x, y, cardW, 90, "F");
    doc.setDrawColor(...accent); doc.setLineWidth(0.7); doc.rect(x, y, cardW, 90, "S");
    doc.setFont("courier", "normal"); doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text(g.label, x + 12, y + 18);
    doc.setFont("helvetica", "bold"); doc.setFontSize(28);
    doc.setTextColor(...accent);
    doc.text(String(g.val), x + 12, y + 60);
  });

  // Bottom info bar
  doc.setFont("courier", "normal"); doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text(`Site: ${SITE.lotAcres} ACRES · ${SITE.lotSF.toLocaleString()} SF · IRREGULAR LOT · ${SITE.location}`, 36, H - 60);
  doc.text("Construction: ISBU shipping container modules · 3 levels · split 40' container = Studio + 1BR · IRC egress · dual stairwells per building", 36, H - 44);

  drawFooter(1);

  // ============================================================
  // PAGE 2 — CONTAINER BILL OF MATERIALS (most important for pricing)
  // ============================================================
  doc.addPage(); fillBg();
  drawHeader("Container Bill of Materials", "Page 2 of 6 · purchase quantities · per site");

  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.setTextColor(...accent);
  doc.text("Per Building (current selection: " + (building.name || buildingId) + ")", 36, 100);

  autoTable(doc, {
    startY: 110,
    head: [["Unit Type", "Container Size", "Qty per Bldg", "Floor", "Notes"]],
    body: building.unit_types.map(u => {
      const count = per_building_by_type[u.id] || 0;
      const floor = u.id === "COMM_40" ? "Ground (Plaza)" : "All levels";
      const NOTES_BY_TYPE = {
        TWOBR_40:  "2BR / 1BA — full 40' container · center wet-wall",
        ONEBR_25:  "1BR · paired with Studio in split 40'",
        STUDIO_15: "Studio efficiency · paired with 1BR in split 40'",
        COMM_40:   "Commercial storefront",
      };
      const notes = NOTES_BY_TYPE[u.id] || "—";
      return [u.label, `${u.length_ft}' × ${u.depth_ft}'`, count, floor, notes];
    }),
    theme: "grid",
    styles: { font: "courier", fontSize: 9, textColor: text, fillColor: card,
              lineColor: [31, 41, 55], lineWidth: 0.4, cellPadding: 5 },
    headStyles: { fillColor: accent, textColor: bgDark, fontStyle: "bold", halign: "center" },
    alternateRowStyles: { fillColor: [11, 18, 32] },
    margin: { left: 36, right: 36 },
  });

  const startY2 = doc.lastAutoTable.finalY + 30;

  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.setTextColor(...accent);
  doc.text("Site Total — All 6 Residential Bars + Retail Strip", 36, startY2);

  autoTable(doc, {
    startY: startY2 + 10,
    head: [["Container Type", "Studios", "1BR", "2BR", "Commercial", "TOTAL SITE"]],
    body: [
      ["40' split (Studio+1BR)", SITE.studios, SITE.ones, "", "", split_containers],
      ["40' full 2BR",           "", "", SITE.twos, "", full_2br],
      ["40' commercial",         "", "", "", SITE.commercial, commercial_containers],
      [{ content: "GRAND TOTAL 40' CONTAINERS", styles: { fontStyle: "bold" } }, "", "", "", "",
       { content: total_containers, styles: { fontStyle: "bold", textColor: good } }],
    ],
    theme: "grid",
    styles: { font: "courier", fontSize: 9, textColor: text, fillColor: card,
              lineColor: [31, 41, 55], lineWidth: 0.4, cellPadding: 5, halign: "center" },
    headStyles: { fillColor: accent, textColor: bgDark, fontStyle: "bold", halign: "center" },
    columnStyles: { 0: { halign: "left", cellWidth: 200 }, 5: { fontStyle: "bold", textColor: good } },
    margin: { left: 36, right: 36 },
  });

  // Notes box
  const noteY = doc.lastAutoTable.finalY + 18;
  doc.setFillColor(...card); doc.rect(36, noteY, W - 72, 60, "F");
  doc.setDrawColor(...muted); doc.setLineWidth(0.4); doc.rect(36, noteY, W - 72, 60, "S");
  doc.setFont("courier", "bold"); doc.setFontSize(9);
  doc.setTextColor(...accent);
  doc.text("NOTES FOR MANUFACTURER:", 46, noteY + 16);
  doc.setFont("courier", "normal"); doc.setFontSize(8.5);
  doc.setTextColor(...text);
  doc.text("• All containers are HC (high-cube, 9'-6\" exterior / ~9'-0\" interior ceiling)", 46, noteY + 30);
  doc.text("• Plumbing wet-walls per A1.0 drawing — see Unit Catalog page", 46, noteY + 42);
  doc.text("• Egress windows (IRC R310) required on every bedroom — fenestration spec to follow", 46, noteY + 54);

  drawFooter(2);

  // ============================================================
  // PAGE 2a — STOREFRONT ROSTER (8 storefronts in the retail strip)
  // ============================================================
  doc.addPage(); fillBg();
  drawHeader("Storefront Roster — Sunrise Retail Strip",
             "8 ground-floor storefronts at the project entrance (north end) · 256 ft continuous strip");

  const storefronts = loadStorefronts();
  const STORE_UNIT_TYPE = building.unit_types.find(u => u.id === "COMM_40");
  const storeRows = storefronts.map((s, i) => [
    String(i + 1).padStart(2, "0"),
    s.name,
    "40' × 8'",
    "320 SF",
    s.color,
    "Ground-floor retail · double front entry",
  ]);

  autoTable(doc, {
    startY: 110,
    head: [["#", "Storefront Name", "Container", "Floor Area", "Facade Color", "Spec"]],
    body: storeRows,
    theme: "grid",
    styles: { font: "courier", fontSize: 9.5, textColor: text, fillColor: card,
              lineColor: [31, 41, 55], lineWidth: 0.4, cellPadding: 6 },
    headStyles: { fillColor: accent, textColor: bgDark, fontStyle: "bold", halign: "center" },
    alternateRowStyles: { fillColor: [11, 18, 32] },
    columnStyles: {
      0: { halign: "center", cellWidth: 36, fontStyle: "bold" },
      1: { halign: "left",   cellWidth: 150, fontStyle: "bold", textColor: accent },
      2: { halign: "center", cellWidth: 70 },
      3: { halign: "center", cellWidth: 70 },
      4: { halign: "center", cellWidth: 110, fontStyle: "bold" },
      5: { halign: "left" },
    },
    margin: { left: 36, right: 36 },
    didDrawCell: (data) => {
      // Render a real color swatch in column 4 instead of just hex text
      if (data.section === "body" && data.column.index === 4) {
        const hex = storefronts[data.row.index].color;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const cx = data.cell.x + 14;
        const cy = data.cell.y + data.cell.height / 2 - 5;
        doc.setFillColor(r, g, b);
        doc.setDrawColor(...muted); doc.setLineWidth(0.3);
        doc.rect(cx, cy, 14, 10, "FD");
      }
    },
  });

  const sRosterY = doc.lastAutoTable.finalY + 18;
  doc.setFillColor(...card); doc.rect(36, sRosterY, W - 72, 60, "F");
  doc.setDrawColor(...muted); doc.setLineWidth(0.4); doc.rect(36, sRosterY, W - 72, 60, "S");
  doc.setFont("courier", "bold"); doc.setFontSize(9);
  doc.setTextColor(...accent);
  doc.text("LEASING NOTES:", 46, sRosterY + 16);
  doc.setFont("courier", "normal"); doc.setFontSize(8.5);
  doc.setTextColor(...text);
  doc.text("• Each storefront occupies one 40' high-cube container — 320 SF leasable + glass front facade", 46, sRosterY + 30);
  doc.text("• Tenants share south-facing arrival plaza (drop-off + pavilion) and 24-stall retail parking", 46, sRosterY + 42);
  doc.text("• Names & facade colors above reflect the operator's current branding — fully editable in the visualizer", 46, sRosterY + 54);

  drawFooter(2);

  // ============================================================
  // PAGE 2b — PER-WING / PER-FLOOR DISTRIBUTION MATRIX (dynamic)
  // ============================================================
  doc.addPage(); fillBg();
  drawHeader("Per-Wing / Per-Floor Unit Distribution",
             "Studios · 1BR · 2BR · Commercial — across wings (read live from backend)");

  // Build matrix dynamically from siteSummary.buildings + current building.wings
  const bldgEntries = siteSummary?.buildings ? Object.entries(siteSummary.buildings) : [];
  const matrixRows = [];
  bldgEntries.forEach(([bid, st]) => {
    matrixRows.push([
      { content: bid, styles: { fontStyle: "bold", fillColor: [22, 30, 48] } },
      { content: st.name || bid, styles: { fillColor: [22, 30, 48] } },
      { content: st.studios, styles: { fillColor: [22, 30, 48] } },
      { content: st.ones, styles: { fillColor: [22, 30, 48] } },
      { content: st.twos, styles: { fillColor: [22, 30, 48] } },
      { content: st.commercial, styles: { fillColor: [22, 30, 48] } },
      { content: st.residential + st.commercial, styles: { fontStyle: "bold", fillColor: [22, 30, 48], textColor: good } },
    ]);
  });
  // Site total row
  matrixRows.push([
    { content: "SITE TOTAL", styles: { fontStyle: "bold", fillColor: accent, textColor: bgDark } },
    { content: "", styles: { fillColor: accent } },
    { content: SITE.studios, styles: { fontStyle: "bold", fillColor: accent, textColor: bgDark } },
    { content: SITE.ones,    styles: { fontStyle: "bold", fillColor: accent, textColor: bgDark } },
    { content: SITE.twos,    styles: { fontStyle: "bold", fillColor: accent, textColor: bgDark } },
    { content: SITE.commercial, styles: { fontStyle: "bold", fillColor: accent, textColor: bgDark } },
    { content: SITE.totalUnits + SITE.commercial, styles: { fontStyle: "bold", fillColor: accent, textColor: bgDark } },
  ]);

  autoTable(doc, {
    startY: 100,
    head: [["Bldg", "Name", "Studio (15')", "1BR (25')", "2BR (40')", "Commercial (40')", "Total"]],
    body: matrixRows,
    theme: "grid",
    styles: { font: "courier", fontSize: 9, textColor: text, fillColor: card,
              lineColor: [31, 41, 55], lineWidth: 0.4, cellPadding: 5, halign: "center" },
    headStyles: { fillColor: accent, textColor: bgDark, fontStyle: "bold", halign: "center" },
    columnStyles: {
      0: { halign: "center", cellWidth: 60, fontStyle: "bold" },
      1: { halign: "left",   cellWidth: 160 },
    },
    margin: { left: 36, right: 36 },
  });

  // Notes
  const distNoteY = doc.lastAutoTable.finalY + 16;
  doc.setFillColor(...card); doc.rect(36, distNoteY, W - 72, 70, "F");
  doc.setDrawColor(...muted); doc.setLineWidth(0.4); doc.rect(36, distNoteY, W - 72, 70, "S");
  doc.setFont("courier", "bold"); doc.setFontSize(9);
  doc.setTextColor(...accent);
  doc.text("NOTES:", 46, distNoteY + 16);
  doc.setFont("courier", "normal"); doc.setFontSize(8.5);
  doc.setTextColor(...text);
  doc.text("• Studio (15') + 1BR (25') always paired in a single split 40' container — counts move together", 46, distNoteY + 30);
  doc.text("• Commercial storefronts located in the dedicated Retail Strip at the project entrance (north end) only", 46, distNoteY + 42);
  doc.text(`• ${total_containers} total containers = ${split_containers} split (Studio+1BR) + ${full_2br} full 2BR + ${commercial_containers} commercial — all 40' HC`, 46, distNoteY + 54);

  drawFooter(3);

  // ============================================================
  // PAGE 3 — UNIT TYPE CATALOG
  // ============================================================
  doc.addPage(); fillBg();
  drawHeader("Unit Type Catalog", "Page 3 of 6 · room areas + features per unit type");

  const catalogRows = building.unit_types.map(u => {
    const beds = u.rooms.filter(r => r.kind === "bedroom" || r.kind === "sleep").length;
    const baths = u.rooms.filter(r => r.kind === "bath").length;
    const eg = u.rooms.filter(r => r.egress_window).length;
    const sqft = Math.round(u.length_ft * u.depth_ft);
    const rooms = u.rooms.map(r => `${r.name} ${r.sf ? `(${r.sf}sf)` : ""}`).join(" · ");
    return [
      u.label,
      `${u.length_ft}' × ${u.depth_ft}'`,
      `${sqft} SF`,
      beds,
      baths,
      eg,
      u.doors.length,
      rooms,
    ];
  });

  autoTable(doc, {
    startY: 90,
    head: [["Unit Type", "Container", "Area", "BR", "BA", "Egr. Wins", "Doors", "Room Composition"]],
    body: catalogRows,
    theme: "grid",
    styles: { font: "courier", fontSize: 8, textColor: text, fillColor: card,
              lineColor: [31, 41, 55], lineWidth: 0.4, cellPadding: 5, valign: "middle" },
    headStyles: { fillColor: accent, textColor: bgDark, fontStyle: "bold", halign: "center", fontSize: 8 },
    alternateRowStyles: { fillColor: [11, 18, 32] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 80 },
      1: { halign: "center", cellWidth: 70 },
      2: { halign: "center", cellWidth: 55 },
      3: { halign: "center", cellWidth: 30 },
      4: { halign: "center", cellWidth: 30 },
      5: { halign: "center", cellWidth: 55 },
      6: { halign: "center", cellWidth: 40 },
      7: { fontSize: 7.5 },
    },
    margin: { left: 36, right: 36 },
  });

  drawFooter(3);

  // ============================================================
  // PAGE 4 — BUILDING SPEC SHEET
  // ============================================================
  doc.addPage(); fillBg();
  drawHeader("Building Specification", "Page 4 of 6 · typical building (×6 identical)");

  const specs = [
    ["Site composition",       "6 × residential bars (stacks) + 1 × retail strip at the entrance (single story)"],
    ["U-building footprint",   "80' wide × 408' deep (3 wings: 2 long + 1 connector at north)"],
    ["Stories",                "3 levels above grade (residential) · 1 level (commercial plaza)"],
    ["Long wing length",       "400'-0\" — 10 split-container slots per floor"],
    ["Connector wing length",  "200'-0\" — 5 split-container slots per floor"],
    ["Total built area · U",   `${(summary.living_sf || 23680).toLocaleString()} SF per U (residential)`],
    ["Floor-to-ceiling",       "9'-0\" interior (HC container)"],
    ["Ground floor units",     "Studios · 1BR · 2BR (residential mix continues from upper floors)"],
    ["Upper floor units",      "Studios + 1BR (split 40' containers) + 2BR (full 40')"],
    ["Units per U-building",   "130 (50 studios + 50 1BR + 30 2BR — typical)"],
    ["Stairwells",             "2 per long wing — exterior steel at outer ends"],
    ["Catwalk",                "Continuous exterior catwalk on entry side, 5'-0\" wide, canopy-covered"],
    ["Balconies",              "Courtyard-facing (interior of U) — 6' sliding glass + 4' deep platform"],
    ["Egress windows",         "All bedrooms — IRC R310 compliant (~5.7 SF clear opening)"],
    ["Commercial",             "8 storefronts in a continuous retail strip at the entrance, multicolored facades"],
    ["Central pavilion",       "Covered restaurant/gathering between the two U courtyards"],
    ["Laundry",                "Container block tucked behind commercial row, opens to courtyard"],
    ["Solar roof (optional)",  "PV array on catwalk + balcony canopies"],
    ["Foundation",             "Slab-on-grade with concrete piers (TBD by structural)"],
    ["Exterior finish",        "Painted steel — white residential / multicolor commercial"],
    ["HVAC",                   "Mini-split per unit (TBD)"],
    ["Electrical",             "+12 kW for golf-cart charging (~20 EV stalls)"],
  ];

  autoTable(doc, {
    startY: 90,
    head: [["Item", "Specification"]],
    body: specs,
    theme: "grid",
    styles: { font: "courier", fontSize: 9, textColor: text, fillColor: card,
              lineColor: [31, 41, 55], lineWidth: 0.4, cellPadding: 6 },
    headStyles: { fillColor: accent, textColor: bgDark, fontStyle: "bold", halign: "left" },
    alternateRowStyles: { fillColor: [11, 18, 32] },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 200 } },
    margin: { left: 36, right: 36 },
  });

  drawFooter(4);

  // ============================================================
  // PAGE 5 — FULL UNIT SCHEDULE (existing)
  // ============================================================
  doc.addPage(); fillBg();
  drawHeader("Unit Schedule — Building " + buildingId.toUpperCase(),
             "Page 5 of 6 · live read from backend · all wings + floors");

  const rows = [];
  (building.wings || []).forEach((w) => {
    (w.floors || []).forEach((f) => {
      f.units.forEach((u) => {
        const ut = ut_map[u.unit_type_id];
        const beds = ut.rooms.filter(r => r.kind === "bedroom" || r.kind === "sleep").length;
        const baths = ut.rooms.filter(r => r.kind === "bath").length;
        const eg = ut.rooms.filter(r => r.egress_window).length;
        rows.push([
          `${f.level}`, w.label, u.label, ut.label,
          `${ut.length_ft}' × ${ut.depth_ft}'`,
          beds, baths, eg, ut.doors.length,
          `${Math.round(ut.length_ft * ut.depth_ft)} SF`,
        ]);
      });
    });
  });

  autoTable(doc, {
    startY: 90,
    head: [["Floor", "Wing", "Unit", "Type", "Container", "BR", "BA", "Egr.", "Doors", "Area"]],
    body: rows,
    theme: "grid",
    styles: { font: "courier", fontSize: 8, textColor: text, fillColor: card,
              lineColor: [31, 41, 55], lineWidth: 0.4, cellPadding: 4 },
    headStyles: { fillColor: accent, textColor: bgDark, fontStyle: "bold", halign: "center", fontSize: 8 },
    alternateRowStyles: { fillColor: [11, 18, 32] },
    columnStyles: {
      0: { halign: "center", cellWidth: 35 },
      1: { halign: "left",   cellWidth: 70 },
      2: { halign: "center", cellWidth: 55, fontStyle: "bold" },
      3: { cellWidth: 70 },
      4: { halign: "center", cellWidth: 65 },
      5: { halign: "center", cellWidth: 25 },
      6: { halign: "center", cellWidth: 25 },
      7: { halign: "center", cellWidth: 35 },
      8: { halign: "center", cellWidth: 40 },
      9: { halign: "right",  cellWidth: 55 },
    },
    margin: { left: 36, right: 36 },
    didDrawPage: () => drawFooter(5),
  });

  // ============================================================
  // PAGE 6 — SITE SUMMARY & PARKING
  // ============================================================
  doc.addPage(); fillBg();
  drawHeader("Site Layout & Parking", "Page 6 of 6 · 2.13-acre site · golf-cart parking 1:1");

  // Two-column layout: left = site facts, right = parking + scope
  const colX = [36, W / 2 + 12];
  const colW = W / 2 - 50;
  let yL = 100, yR = 100;

  // Left: site dimensions
  doc.setFillColor(...card); doc.rect(colX[0], yL, colW, 200, "F");
  doc.setDrawColor(...accent); doc.setLineWidth(0.7); doc.rect(colX[0], yL, colW, 200, "S");
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.setTextColor(...accent);
  doc.text("LOT DIMENSIONS (FROM SURVEY)", colX[0] + 14, yL + 22);

  const dims = [
    ["Street frontage",     "57 ft"],
    ["Neck — west / east",  "77 / 79 ft"],
    ["Widen jog — W / E",   "171 / 56 ft"],
    ["Long side — W / E",   "331 / 308 ft"],
    ["South edge",          "280 ft"],
    ["Total area",          "2.13 acres / 95,832 SF"],
    ["Site coverage",       "23,040 SF footprint (24% built)"],
    ["Open / landscape",    "~72,800 SF (76%)"],
  ];
  doc.setFont("courier", "normal"); doc.setFontSize(9);
  dims.forEach((d, i) => {
    const y = yL + 44 + i * 16;
    doc.setTextColor(...muted); doc.text(d[0], colX[0] + 14, y);
    doc.setTextColor(...text); doc.text(d[1], colX[0] + colW - 14, y, { align: "right" });
  });

  // Right: parking breakdown
  doc.setFillColor(...card); doc.rect(colX[1], yR, colW, 200, "F");
  doc.setDrawColor(34, 211, 238); doc.setLineWidth(0.7); doc.rect(colX[1], yR, colW, 200, "S");
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.setTextColor(34, 211, 238);
  doc.text("GOLF-CART PARKING (1:1 RATIO)", colX[1] + 14, yR + 22);

  const parking = [
    ["Required (Belize SI 157/2022 Reg 6(b))", `${SITE.parkingTotal} stalls`],
    ["Basis",                                   "1 stall / 500 SF habitable"],
    ["Habitable area",                          `${SITE.livingSF.toLocaleString()} SF`],
    ["Provided · 4 surface strips + entry lot", "100 stalls"],
    ["EV-equipped (every 4th)",                 "~25 stalls"],
    ["Charger spec",                            "48V × 10A = 480 W ea"],
    ["Charging load",                           "~12 kW (spec 20 kW service)"],
    ["Note",                                    "Golf-cart reduced standard per SPTBU"],
  ];
  doc.setFont("courier", "normal"); doc.setFontSize(9);
  parking.forEach((p, i) => {
    const y = yR + 44 + i * 16;
    const highlight = p[0] === "TOTAL PROVIDED" || p[0].startsWith("EV-");
    doc.setTextColor(...(highlight ? good : muted)); doc.text(p[0], colX[1] + 14, y);
    doc.setTextColor(...(highlight ? good : text)); doc.text(p[1], colX[1] + colW - 14, y, { align: "right" });
  });

  // Bottom: scope of work
  const scopeY = yL + 220;
  doc.setFillColor(...card); doc.rect(36, scopeY, W - 72, 190, "F");
  doc.setDrawColor(...accent); doc.setLineWidth(0.7); doc.rect(36, scopeY, W - 72, 190, "S");
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.setTextColor(...accent);
  doc.text("SCOPE FOR MANUFACTURER QUOTE", 50, scopeY + 22);

  const scope = [
    "1. SUPPLY OF SHIPPING CONTAINERS — all 40' HC",
    "   • " + split_containers + " × 40' containers configured as SPLIT (15' Studio + 25' 1BR partitioned interior)",
    "   • " + full_2br + " × 40' containers configured as FULL 2BR",
    "   • " + commercial_containers + " × 40' containers configured as COMMERCIAL storefronts",
    "   • Total: " + total_containers + " × 40' HC containers",
    "",
    "2. CONTAINER FIT-OUT (per unit-type catalog)",
    "   • Interior partitions, finishes, plumbing rough-ins",
    "   • Cut openings: entry doors, windows (IRC R310 egress at bedrooms), balcony sliding doors on upper floors",
    "   • Mini-split provisions",
    "",
    "3. STACKING & STRUCTURAL CONNECTIONS",
    "   • 3-story stacking · " + SITE.buildings + " buildings · ISBU corner-cast welds",
    "",
    "4. EGRESS / STAIRS / BALCONIES",
    "   • 2 × exterior steel stairwells per building (" + (SITE.buildings * 2) + " total)",
    "   • Steel balcony platforms + railings on upper floors",
    "",
    "5. ROOF / OPTIONAL SOLAR + STORAGE",
    "   • Solar PV on breezeway + L3/L4 balcony canopy (~480-560 kWp installed across 6 buildings)",
    "   • Battery Energy Storage System (BESS) — 200 kWh LFP (Lithium Iron Phosphate) lithium bank for night-time cart charging + common-area backup",
    "   • LFP chemistry selected for thermal stability in Belize tropical climate (vs NMC)",
    "   • Pad-mounted near the parking pavilion · weatherproof IP-65 enclosure · BMS + grid-tie inverter included",
  ];
  doc.setFont("courier", "normal"); doc.setFontSize(8.5);
  doc.setTextColor(...text);
  scope.forEach((line, i) => {
    doc.text(line, 50, scopeY + 42 + i * 11);
  });

  drawFooter(6);

  // ============================================================
  // PAGES 7+ — VISUAL SNAPSHOTS (3D, 2D, Site)
  // ============================================================
  let pageNum = 6;
  for (const snap of snapshots) {
    if (!snap || !snap.dataURL) continue;
    pageNum += 1;
    doc.addPage(); fillBg();
    drawHeader(snap.title, `Page ${pageNum} · captured from interactive visualizer`);

    // Embed image fitted within page with margins
    try {
      const imgMargin = 36;
      const maxW = W - imgMargin * 2;
      const maxH = H - 110 - 40;  // account for header + footer
      // Probe size from a temporary Image
      const img = new Image();
      img.src = snap.dataURL;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => { img.onload = res; img.onerror = res; });
      const iw = img.naturalWidth || maxW;
      const ih = img.naturalHeight || maxH;
      const scale = Math.min(maxW / iw, maxH / ih);
      const drawW = iw * scale;
      const drawH = ih * scale;
      const drawX = (W - drawW) / 2;
      const drawY = 90 + (maxH - drawH) / 2;
      doc.addImage(snap.dataURL, "PNG", drawX, drawY, drawW, drawH, undefined, "FAST");
    } catch (e) {
      doc.setFont("courier", "normal"); doc.setFontSize(10);
      doc.setTextColor(...muted);
      doc.text(`(Image render failed: ${String(e).slice(0, 80)})`, 36, 200);
    }
    drawFooter(pageNum);
  }

  doc.save(`sunrise_${(buildingId || "a").toLowerCase()}_manufacturer_quote.pdf`);
}
